(() => {
  const earlyPass = document.readyState === "loading";
  if (!earlyPass && window.__vorugPdpMediaLoaded) return;
  const ROOM_LABELS = { narrow: "Narrow hallway", wide: "Wide hallway", foyer: "Foyer", dining: "Dining room" };
  const ROOM_SETS = {
    runner: ["narrow", "wide", "foyer"],
    rug: ["dining", "foyer", "wide"],
    round: ["foyer", "dining"],
  };

  // Classify only authored photo metadata; unknown/video/demo cards retain stable order.
  const mediaRole = (card) => {
    if (card.querySelector?.("[data-room-lab-demo]")) return "unknown";
    const image = card.querySelector?.("img");
    if (!image) return "unknown";
    const filename = (image.getAttribute("src") || "").split("?")[0].split("/").pop().toLowerCase();
    const alt = (image.getAttribute("alt") || "").toLowerCase();
    // Explicit native filename roles outrank incidental material words in captions.
    if (/-(room|lifestyle)(-\d+)?[.-]/.test(filename)) return "room";
    if (/-label[.-]/.test(filename)) return "underside";
    if (/-corner[.-]|-fringe[.-]/.test(filename)) return "fringe";
    if (/-texture[.-]/.test(filename)) return "texture";
    if (/\b(furnished room|lived-in room|styled in|in a home)\b/.test(alt)) return "room";
    if (/\b(underside|label|backing)\b/.test(alt)) return "underside";
    if (/\b(fringe|tassels|edge finish|finished edge)\b/.test(alt)) return "fringe";
    if (/\b(close-up|close up|wool pile|texture)\b/.test(alt)) return "texture";
    return "unknown";
  };
  const orderMediaCards = (cards, rail) => {
    const priority = { room: 0, unknown: 1, texture: 2, fringe: 3, underside: 4 };
    const ordered = cards.map((card, index) => ({ card, index, role: mediaRole(card) }))
      .sort((a, b) => priority[a.role] - priority[b.role] || a.index - b.index);
    if (ordered.some((entry, index) => entry.card !== cards[index])) {
      ordered.forEach(({ card }) => rail.appendChild(card));
    }
    return ordered.map(({ card, role }) => { if (card.dataset) card.dataset.mediaRole = role; return card; });
  };

  const readProduct = (section) => {
    try {
      const raw = section.querySelector("[data-room-lab-gallery-product]")?.textContent || "";
      return JSON.parse(raw.replace(/<!--[\s\S]*?-->/g, "").trim());
    } catch {
      return undefined;
    }
  };

  const selectedVariantId = () => {
    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return String(input?.value || new URLSearchParams(location.search).get("variant") || "").split("/").pop();
  };

  // Top product gallery: slides through every product image with arrows, a counter,
  // keyboard and touch/drag support. Replaces the old "primary image only" behaviour.
  const GALLERY_ARROW = '<svg class="vorug-gallery__icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14m-5-5 5 5-5 5"></path></svg>';
  const initGallery = (gallery) => {
    const list = gallery.querySelector('[id^="Slider-Gallery-"].product__media-list');
    const component = gallery.querySelector('slider-component[id^="GalleryViewer"]');
    const viewport = gallery.querySelector('[id^="GalleryViewer"] .slider__viewport');
    if (!list || !component || !viewport) return;
    const existing = gallery._vorugGallery;
    if (existing) {
      if (existing.list === list && existing.viewport === viewport && existing.controls.isConnected && component.contains(existing.controls)) { existing.refresh(); return; }
      existing.destroy();
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const abort = new AbortController();
    const listen = (target, type, handler, options) => target.addEventListener(type, handler, { ...(options || {}), signal: abort.signal });
    let slides = [];
    let index = 0;
    let targetIndex = 0;
    let width = 0;
    let step = 0;
    let maximum = 0;
    let rtl = false;
    let navigating = false;
    let scrollRaf = 0;
    let scrollTimer = 0;
    let wheelTimer = 0;
    let clickTimer = 0;
    let wheel = null;
    let start = null;
    let suppressClick = false;
    const controls = document.createElement('div');
    controls.className = 'vorug-gallery__controls';
    controls.innerHTML =
      '<p class="vorug-gallery__count" role="status" aria-live="polite" aria-atomic="true">' +
        '<span data-gallery-current>1</span><span class="vorug-gallery__sep" aria-hidden="true"> | </span><span class="visually-hidden"> / </span><span data-gallery-total>1</span></p>' +
      '<div class="vorug-gallery__arrows">' +
        '<button type="button" class="vorug-gallery__arrow vorug-gallery__arrow--prev" data-gallery-prev aria-label="Previous image">' + GALLERY_ARROW + '</button>' +
        '<button type="button" class="vorug-gallery__arrow vorug-gallery__arrow--next" data-gallery-next aria-label="Next image">' + GALLERY_ARROW + '</button>' +
      '</div>';
    component.appendChild(controls);
    const current = controls.querySelector('[data-gallery-current]');
    const total = controls.querySelector('[data-gallery-total]');
    const previous = controls.querySelector('[data-gallery-prev]');
    const next = controls.querySelector('[data-gallery-next]');
    const clamp = (value) => Math.max(0, Math.min(slides.length - 1, value));
    const position = () => Math.max(0, Math.min(maximum, viewport.scrollLeft * (rtl ? -1 : 1)));
    const scrollTo = (value, behavior = 'smooth') => viewport.scrollTo({ left: value * (rtl ? -1 : 1), behavior: reduced.matches ? 'instant' : behavior });
    const render = () => {
      slides.forEach((slide, i) => {
        const active = i === index;
        if (!active && slide.contains(document.activeElement)) viewport.focus({ preventScroll: true });
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        slide.toggleAttribute('inert', !active);
      });
      const label = String(index + 1);
      if (current.textContent !== label) current.textContent = label;
      previous.disabled = targetIndex <= 0;
      next.disabled = targetIndex >= slides.length - 1;
      [index - 1, index, index + 1].forEach((i) => slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));
    };
    const stopWheel = () => {
      clearTimeout(wheelTimer);
      wheel = null;
    };
    const finish = () => {
      if (wheel || start || viewport.clientWidth !== Math.round(width)) return;
      index = clamp(Math.round(position() / (step || 1)));
      targetIndex = index;
      navigating = false;
      viewport.classList.remove('is-controlled-scrolling');
      render();
    };
    const goTo = (target, behavior = 'smooth') => {
      stopWheel();
      targetIndex = clamp(target);
      navigating = true;
      render();
      const destination = Math.min(maximum, targetIndex * step);
      scrollTo(destination, behavior);
      if (Math.abs(position() - destination) < 1) finish();
    };
    const measure = () => {
      width = viewport.getBoundingClientRect().width;
      step = slides.length > 1 ? Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left) : width;
      maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      rtl = getComputedStyle(gallery).direction === 'rtl';
    };
    const refresh = () => {
      stopWheel();
      slides = [...list.children].filter((item) => item.classList.contains('product__media-item'));
      total.textContent = String(slides.length);
      gallery.toggleAttribute('data-vorug-gallery-single', slides.length < 2);
      index = clamp(Math.max(0, slides.findIndex((item) => item.classList.contains('is-active'))));
      measure();
      slides.forEach((slide) => slide.querySelectorAll('img').forEach((img) => { img.decoding = 'async'; }));
      goTo(index, 'instant');
    };
    listen(previous, 'click', () => goTo(targetIndex - 1));
    listen(next, 'click', () => goTo(targetIndex + 1));
    listen(gallery, 'keydown', (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.target.closest?.('input, textarea, select, video, model-viewer')) return;
      if (event.key === 'ArrowRight') goTo(targetIndex + (rtl ? -1 : 1));
      else if (event.key === 'ArrowLeft') goTo(targetIndex + (rtl ? 1 : -1));
      else if (event.key === 'Home') goTo(0);
      else if (event.key === 'End') goTo(slides.length - 1);
      else return;
      event.preventDefault();
      event.stopPropagation();
    });
    listen(viewport, 'scroll', () => {
      if (!scrollRaf) scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        if (viewport.clientWidth !== Math.round(width)) return;
        const active = clamp(Math.round(position() / (step || 1)));
        if (active === index) return;
        index = active;
        if (!navigating) targetIndex = index;
        render();
      });
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(finish, 150);
    }, { passive: true });
    listen(viewport, 'scrollend', finish, { passive: true });
    listen(viewport, 'wheel', (event) => {
      if (event.defaultPrevented || event.ctrlKey || start || slides.length < 2 || event.target.closest?.('video, iframe, model-viewer, input, textarea, select')) return;
      if (event.shiftKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
        stopWheel();
        viewport.classList.remove('is-controlled-scrolling');
        navigating = false;
        return;
      }
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? width : 1;
      const delta = event.deltaY * unit;
      const actual = position();
      if (!delta || (!wheel && ((delta < 0 && actual <= 1) || (delta > 0 && actual >= maximum - 1)))) return;
      event.preventDefault();
      if (!wheel) {
        wheel = { origin: Math.round(actual / step), target: actual, direction: 0 };
        viewport.classList.add('is-controlled-scrolling');
      }
      navigating = true;
      wheel.direction = Math.sign(delta);
      wheel.target = Math.max(0, Math.min(maximum, wheel.target + delta));
      scrollTo(wheel.target, 'instant');
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        let target = Math.round(wheel.target / step);
        if (target === wheel.origin && Math.abs(wheel.target - Math.min(maximum, wheel.origin * step)) > 8) target += wheel.direction;
        goTo(target);
      }, 140);
    }, { passive: false });
    const endDrag = () => {
      const pointerId = start?.id;
      start = null;
      viewport.classList.remove('is-dragging');
      if (pointerId !== undefined && viewport.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
    };
    listen(viewport, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || slides.length < 2 || event.target.closest?.('video, iframe, model-viewer, input, textarea, select, a')) return;
      if (start) endDrag();
      stopWheel();
      clearTimeout(scrollTimer);
      const actual = position();
      viewport.classList.add('is-controlled-scrolling');
      navigating = false;
      scrollTo(actual, 'instant');
      start = { x: event.clientX, y: event.clientY, position: actual, lastPosition: actual, lastTime: performance.now(), velocity: 0, id: event.pointerId, dragging: false };
    });
    listen(window, 'pointermove', (event) => {
      if (!start || event.pointerId !== start.id) return;
      if (event.buttons === 0) { cancelDrag(event); return; }
      const dx = event.clientX - start.x;
      if (!start.dragging) {
        if (Math.abs(dx) < 4 || Math.abs(dx) < Math.abs(event.clientY - start.y)) return;
        start.dragging = true;
        navigating = false;
        viewport.classList.add('is-dragging', 'is-controlled-scrolling');
        viewport.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      const actual = Math.max(0, Math.min(maximum, start.position - dx * (rtl ? -1 : 1)));
      const now = performance.now();
      const distance = actual - start.lastPosition;
      if (distance) {
        const elapsed = now - start.lastTime;
        const velocity = distance / Math.max(1, Math.min(80, elapsed));
        start.velocity = elapsed < 80 && Math.sign(velocity) === Math.sign(start.velocity) ? start.velocity * .35 + velocity * .65 : velocity;
        start.lastPosition = actual;
        start.lastTime = now;
      }
      scrollTo(actual, 'instant');
    }, { passive: false });
    const releaseDrag = (event) => {
      if (!start || event.pointerId !== start.id) return;
      const drag = start;
      endDrag();
      if (!drag.dragging) { goTo(Math.round(position() / step)); return; }
      suppressClick = true;
      clearTimeout(clickTimer);
      clickTimer = setTimeout(() => { suppressClick = false; }, 60);
      const distance = position() - drag.position;
      const velocity = performance.now() - drag.lastTime < 100 ? drag.velocity : 0;
      const projection = Math.abs(distance) < 8 ? 0 : Math.max(-step * .35, Math.min(step * .35, velocity * 180));
      let target = Math.round((position() + projection) / step);
      const origin = Math.round(drag.position / step);
      if (target === origin && Math.abs(distance) >= 8) target += Math.sign(distance);
      goTo(target);
    };
    listen(viewport, 'pointerup', releaseDrag);
    listen(window, 'pointerup', releaseDrag, { passive: true });
    const cancelDrag = (event) => {
      if (!start || (event?.pointerId !== undefined && event.pointerId !== start.id)) return;
      endDrag();
      goTo(Math.round(position() / step));
    };
    listen(viewport, 'pointercancel', cancelDrag);
    listen(viewport, 'lostpointercapture', cancelDrag);
    listen(window, 'blur', cancelDrag);
    listen(viewport, 'touchstart', () => { stopWheel(); navigating = false; viewport.classList.remove('is-controlled-scrolling'); }, { passive: true });
    listen(viewport, 'dragstart', (event) => event.preventDefault());
    listen(viewport, 'click', (event) => { if (suppressClick) { event.preventDefault(); event.stopPropagation(); } }, true);
    let syncRaf = 0;
    const observer = new MutationObserver(() => {
      if (syncRaf) return;
      syncRaf = requestAnimationFrame(() => {
        syncRaf = 0;
        const live = [...list.children].filter((item) => item.classList.contains('product__media-item'));
        if (gallery.isConnected && (live.length !== slides.length || live.some((item, i) => item !== slides[i]))) refresh();
      });
    });
    observer.observe(list, { childList: true });
    const resize = new ResizeObserver(() => {
      const liveWidth = viewport.getBoundingClientRect().width;
      const liveStep = slides.length > 1 ? Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left) : liveWidth;
      if (liveWidth === width && liveStep === step) return;
      measure();
      goTo(index, 'instant');
    });
    resize.observe(viewport);
    const destroy = () => {
      abort.abort();
      observer.disconnect();
      resize.disconnect();
      cancelAnimationFrame(syncRaf);
      cancelAnimationFrame(scrollRaf);
      clearTimeout(scrollTimer);
      clearTimeout(wheelTimer);
      clearTimeout(clickTimer);
      endDrag();
      viewport.classList.remove('is-controlled-scrolling');
      controls.remove();
      delete gallery._vorugGallery;
    };
    viewport.setAttribute('tabindex', '0');
    gallery.setAttribute('data-vorug-gallery', 'ready');
    gallery.setAttribute('aria-roledescription', 'carousel');
    gallery._vorugGallery = { refresh, goTo, destroy, list, viewport, controls };
    refresh();
    gallery.dispatchEvent(new CustomEvent('vorug:gallery-ready'));
  };
  // One broken gallery must never stop the rest of the page script (rail, policies) from running.
  const initGalleries = () => document.querySelectorAll('[id^="MediaGallery-"]').forEach((gallery) => {
    try { initGallery(gallery); } catch (error) { console.error("[vorug-gallery]", error); }
  });

  let policyPanelSequence = 0;
  const enhancePolicies = (root = document) => {
    root.querySelectorAll("[data-vorug-pdp] .vorug-pdp-policies accordion-block").forEach((block) => {
      const toggle = block.querySelector(".accordion-toggle");
      const summary = toggle?.querySelector(".accordion__summary");
      const panel = toggle?.querySelector(".accordion__panel");
      if (!summary || !panel || toggle.classList.contains("not_collapsible") || summary.dataset.vorugPolicyAccessible === "true") return;
      summary.dataset.vorugPolicyAccessible = "true";
      const nativeButton = summary.matches("button, input, summary");
      if (!nativeButton) {
        summary.setAttribute("role", "button");
        if (!summary.hasAttribute("tabindex")) summary.setAttribute("tabindex", "0");
      }
      if (!panel.id) {
        let id;
        do { id = `vorug-pdp-policy-panel-${++policyPanelSequence}`; } while (document.getElementById(id));
        panel.id = id;
      }
      summary.setAttribute("aria-controls", panel.id);
      const policies = block.closest('.vorug-pdp-policies');
      policies.policyItems ||= Array.from(policies.children);
      const desktop = window.matchMedia('(min-width: 750px)');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const syncExpanded = () => {
        const open = toggle.classList.contains('is-open');
        summary.setAttribute('aria-expanded', String(open));
        panel.setAttribute('aria-hidden', String(!open));
        if (open && desktop.matches) {
          policies.querySelectorAll('accordion-block').forEach((other) => {
            if (other === block) return;
            const otherToggle = other.querySelector('.accordion-toggle');
            if (!otherToggle?.classList.contains('is-open')) return;
            otherToggle.classList.remove('is-open');
            other.querySelector('.accordion__panel').style.maxHeight = '';
            other.blurElements?.();
          });
        }
        const active = policies.querySelector('.accordion-toggle.is-open')?.closest('.product__accordion');
        const focused = policies.contains(document.activeElement) ? document.activeElement : null;
        const layoutWillChange = active && desktop.matches
          ? policies.policyActive?.firstElementChild !== active || policies.policyActive?.parentElement !== policies
          : policies.firstElementChild !== policies.policyItems[0];
        const previousPositions = policies.policyLayoutReady && !reducedMotion.matches && layoutWillChange
          ? new Map(policies.policyItems.map(item => [item, item.getBoundingClientRect()])) : null;
        let layoutChanged = false;
        policies.dataset.policyOpen = String(!!active && desktop.matches);
        policies.policyItems.forEach((item) => {
          item.classList.toggle('is-focused-policy', item === active);
        });
        if (active && desktop.matches) {
          if (!policies.policyActive) {
            policies.policyActive = document.createElement('div');
            policies.policyActive.className = 'vorug-pdp-policy-active';
            policies.policyStack = document.createElement('div');
            policies.policyStack.className = 'vorug-pdp-policy-stack';
          }
          if (policies.policyActive.firstElementChild !== active || policies.policyActive.parentElement !== policies) {
            policies.policyActive.replaceChildren(active);
            policies.policyStack.replaceChildren(...policies.policyItems.filter(item => item !== active));
            policies.replaceChildren(policies.policyActive, policies.policyStack);
            layoutChanged = true;
          }
        } else if (policies.firstElementChild !== policies.policyItems[0]) {
          policies.replaceChildren(...policies.policyItems);
          layoutChanged = true;
        }
        policies.querySelectorAll('accordion-block').forEach((item) => {
          const itemPanel = item.querySelector('.accordion__panel');
          itemPanel.style.maxHeight = item.querySelector('.accordion-toggle.is-open') ? `${itemPanel.scrollHeight}px` : '';
        });
        if (previousPositions && layoutChanged) {
          policies.policyAnimations?.forEach(animation => animation.cancel());
          policies.policyAnimations = policies.policyItems.flatMap(item => {
            const before = previousPositions.get(item);
            const after = item.getBoundingClientRect();
            const x = before.left - after.left;
            const y = before.top - after.top;
            if ((!x && !y) || !item.animate) return [];
            return [item.animate([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'translate(0, 0)' }], { duration: 240, easing: 'cubic-bezier(.22, 1, .36, 1)' })];
          });
        }
        policies.policyLayoutReady = true;
        if (focused?.isConnected && document.activeElement !== focused) focused.focus({ preventScroll: true });
      };
      const onSpace = (event) => {
        if (nativeButton || event.target !== summary || (event.key !== " " && event.code !== "Space")) return;
        event.preventDefault();
        if (!event.repeat) summary.click();
      };
      summary.addEventListener("keydown", onSpace);
      const observer = new MutationObserver(syncExpanded);
      observer.observe(toggle, { attributes: true, attributeFilter: ["class"] });
      const contentObserver = new ResizeObserver(() => {
        if (toggle.classList.contains('is-open')) panel.style.maxHeight = `${panel.scrollHeight}px`;
      });
      contentObserver.observe(panel.firstElementChild);
      desktop.addEventListener('change', syncExpanded);
      syncExpanded();
      block.addEventListener("vorug:policy-cleanup", () => {
        observer.disconnect();
        contentObserver.disconnect();
        policies.policyAnimations?.forEach(animation => animation.cancel());
        desktop.removeEventListener('change', syncExpanded);
        summary.removeEventListener("keydown", onSpace);
        delete summary.dataset.vorugPolicyAccessible;
      }, { once: true });
    });
  };

  const enhanceHelpPopup = (root = document) => {
    const closeLabel = document.querySelector('cart-drawer .drawer__close[aria-label]')?.getAttribute("aria-label")
      || document.querySelector('[data-mto-close][aria-label]')?.getAttribute("aria-label")
      || document.querySelector('product-modal .product-media-modal__toggle[aria-label]')?.getAttribute("aria-label");
    root.querySelectorAll("[data-vorug-pdp] .vorug-pdp-policies modal-opener[data-modal]").forEach((opener) => {
      if (!opener.querySelector(".popup-button--form-question")) return;
      const target = opener.getAttribute("data-modal");
      if (!target?.startsWith("#")) return;
      const modal = document.getElementById(target.slice(1));
      const dialog = modal?.querySelector('[role="dialog"]');
      const heading = modal?.querySelector('.content-heading, h1, h2, h3, [role="heading"]');
      if (dialog && heading?.textContent.trim() && !dialog.hasAttribute("aria-labelledby") && !dialog.hasAttribute("aria-label")) {
        if (!heading.id) {
          let id = `${modal.id}-heading`;
          while (document.getElementById(id)) id = `${modal.id}-heading-${++policyPanelSequence}`;
          heading.id = id;
        }
        dialog.setAttribute("aria-labelledby", heading.id);
      }
      const close = modal?.querySelector(".close-popup");
      if (close && closeLabel?.trim() && !close.hasAttribute("aria-label") && !close.hasAttribute("aria-labelledby") && !close.textContent.trim()) {
        close.setAttribute("aria-label", closeLabel);
      }
    });
  };

  // The app rail does not expose media IDs. Match only unique, exact Shopify
  // image paths emitted from this product's explicitly marked media IDs.
  const planFinishCards = (section, cards) => {
    const maps = [...document.querySelectorAll("[data-vorug-rail-finish-map]")]
      .filter(node => node.dataset.productId === section.dataset.productId);
    if (maps.length !== 1) return null;
    let entries;
    try { entries = JSON.parse(maps[0].textContent); } catch { return null; }
    if (!Array.isArray(entries) || !entries.length) return null;
    const path = value => {
      if (typeof value !== "string" || !value) return null;
      try { const url = new URL(value, document.baseURI); return `${url.origin}${url.pathname}?v=${url.searchParams.get("v") || ""}`; } catch { return null; }
    };
    const known = new Map();
    for (const entry of entries) {
      const key = entry && path(entry.url);
      if (!key || typeof entry.finish !== "boolean" || known.has(key)) return null;
      known.set(key, entry.finish);
    }
    const seen = new Map();
    const removals = [];
    for (const card of cards) {
      if (card.matches("[data-room-lab-demo]") || card.querySelector("[data-room-lab-demo]") || !card.querySelector(".vorug-product-media__asset--image")) continue;
      const images = card.querySelectorAll("img");
      if (images.length !== 1) return null;
      const key = path(images[0].getAttribute("src"));
      if (!key || !known.has(key) || seen.has(key)) return null;
      seen.set(key, true);
      if (known.get(key)) removals.push(card);
    }
    return removals;
  };

  const filterFinishCards = (section, cards) => {
    const removals = planFinishCards(section, cards);
    if (!removals) return cards;
    removals.forEach(card => card.remove());
    return cards.filter(card => !removals.includes(card));
  };

  const initialize = (section) => {
    if (section.dataset.mediaRailReady === "true") return;
    const originalRail = section.querySelector("[data-media-rail]");
    const originalCards = originalRail ? [...originalRail.childNodes] : [];
    const attrs = node => [...node.attributes].map(a => [a.name, a.value]);
    const snapshotNodes = [section, ...section.querySelectorAll("*")];
    const originalAttributes = new Map(snapshotNodes.map(node => [node, attrs(node)]));
    const variantLabels = new Map([...section.querySelectorAll("[data-demo-variant]")].map(node => [node, node.textContent]));
    const restoreAttributes = (node, snapshot) => { [...node.attributes].forEach(attr => node.removeAttribute(attr.name)); snapshot.forEach(([name, value]) => node.setAttribute(name, value)); };
    const originalSectionStyle = section.getAttribute("style");
    const originalRailStyle = originalRail?.getAttribute("style");
    const abort = new AbortController();
    let rollbackObserver = null;
    let removalObserver = null;
    let updateRaf = 0, measureRaf = 0, disposed = false;
    const runUpdate = (update) => {
      if (disposed || !section.isConnected) return;
      try { update(); } catch (error) { console.error("[vorug-media-update]", error); }
    };
    const listen = (target, type, listener, options = {}) => target?.addEventListener(type, listener, { ...(typeof options === "boolean" ? { capture: options } : options), signal: abort.signal });
    try {
    section.dataset.mediaRailReady = "true";
    section.dataset.pdpOptimized = "true";
    const rail = section.querySelector("[data-media-rail]");
    const viewport = section.querySelector("[data-media-viewport]");
    let cards = [...section.querySelectorAll("[data-media-card]")];
    const previous = section.querySelector("[data-media-previous]");
    const next = section.querySelector("[data-media-next]");
    if (!rail || !viewport || !cards.length) throw new Error("Incomplete media rail");
    cards = filterFinishCards(section, cards);
    if (!cards.length) {
      // Every card was an explicitly marked finish; there is no ordinary rail.
      section.hidden = true;
      section.style.display = "none";
      listen(section, "vorug:media-cleanup", () => {
        abort.abort();
        if (originalRail) originalCards.forEach(node => originalRail.appendChild(node));
        originalAttributes.forEach((snapshot, node) => restoreAttributes(node, snapshot));
      }, { once: true });
      return;
    }
    cards = orderMediaCards(cards, rail);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const isRtl = getComputedStyle(rail).direction === "rtl";
    let metrics = { maximum: 0, step: 0, offsets: [] };
    const step = () => metrics.step;
    const maxScroll = () => metrics.maximum;
    const measureRail = () => {
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
      const widths = cards.map((card) => card.getBoundingClientRect().width);
      const width = widths.reduce((total, value) => total + value, 0) + gap * Math.max(0, cards.length - 1);
      let offset = 0;
      const offsets = widths.map((value) => { const start = offset; offset += value + gap; return start; });
      metrics = { maximum: Math.max(0, width - viewport.clientWidth), step: (widths[0] || viewport.clientWidth) + gap, offsets };
    };
    let lastPosition = NaN;
    const setStyle = (name, value) => {
      if (section.style.getPropertyValue(name) !== value) section.style.setProperty(name, value);
    };
    const setProgress = (position) => {
      const maximum = maxScroll();
      const ratio = maximum ? Math.min(1, Math.max(0, position / maximum)) : 1;
      setStyle("--vorug-media-progress", String(ratio));
      section.dataset.railAtStart = String(ratio <= 0.002);
      section.dataset.railAtEnd = String(ratio >= 0.998);
      return ratio;
    };
    const syncPosition = (position) => {
      const max = Math.max(0, maxScroll() - 2);
      if (previous) previous.disabled = position <= 2;
      if (next) next.disabled = position >= max;
      setProgress(position);
    };
    const sticky = section.querySelector("[data-media-sticky]");
    let pin = { start: 0, offset: 0, travel: 0, height: 0 };
    // Geometry is refreshed only on layout changes; scrolling performs no layout reads.
    const pinStart = () => pin.start;
    const pinnedPosition = () => Math.min(1, Math.max(0, (window.scrollY - pinStart()) / Math.max(1, pin.travel))) * maxScroll();
    const setPinActive = (active) => {
      if (section.dataset.pinActive === String(active)) return;
      section.dataset.pinActive = String(active);
      section.dispatchEvent(new CustomEvent("vorug:media-pin-change", { bubbles: true, detail: { active } }));
    };
    const updateArrows = () => {
      const pinned = section.dataset.scrollMode === "pinned";
      setPinActive(pinned && window.scrollY >= pin.start && window.scrollY < pin.start + pin.travel);
      const position = pinned ? pinnedPosition() : Math.abs(rail.scrollLeft);
      if (position === lastPosition) return;
      lastPosition = position;
      if (pinned) rail.style.transform = `translate3d(${position * (isRtl ? 1 : -1)}px, 0, 0)`;
      syncPosition(position);
    };
    const queuePositionUpdate = () => {
      if (!updateRaf) updateRaf = requestAnimationFrame(() => {
        updateRaf = 0;
        runUpdate(updateArrows);
      });
    };
    const goToPosition = (position) => {
      const target = Math.min(maxScroll(), Math.max(0, position));
      if (section.dataset.scrollMode === "pinned") {
        window.scrollTo({ top: pinStart() + target / Math.max(1, maxScroll()) * pin.travel, behavior: reducedMotion.matches ? "auto" : "smooth" });
      } else {
        rail.scrollTo({ left: target * (isRtl ? -1 : 1), behavior: reducedMotion.matches ? "auto" : "smooth" });
      }
    };
    const scrollByCard = (direction) => goToPosition((section.dataset.scrollMode === "pinned" ? pinnedPosition() : Math.abs(rail.scrollLeft)) + direction * step());
    const configureRail = () => {
      const headerHeight = document.querySelector("#shopify-section-header")?.getBoundingClientRect().height || 0;
      setStyle("--pdp-gallery-top", `${headerHeight}px`);
      measureRail();
      // The rail no longer pins the page or converts vertical scrolling into sideways travel; it scrolls normally and keeps its arrows.
      const shouldPin = false;
      section.dataset.scrollMode = shouldPin ? "pinned" : "free";
      section.dataset.scrollDriver = shouldPin ? "js" : "free";
      if (shouldPin) {
        rail.scrollLeft = 0;
        const intro = section.querySelector(".vorug-pdp-details-link")?.getBoundingClientRect().height || 0;
        const height = sticky.getBoundingClientRect().height;
        const travel = maxScroll() * 1.35;
        pin = { start: section.getBoundingClientRect().top + window.scrollY + intro - headerHeight, offset: intro - headerHeight, travel, height };
        setStyle("--vorug-media-rail-width", `${metrics.maximum + viewport.clientWidth}px`);
        setStyle("--vorug-media-scroll-height", `${intro + height + travel}px`);
      } else {
        rail.style.removeProperty("transform");
        section.style.removeProperty("--vorug-media-rail-width");
        section.style.removeProperty("--vorug-media-scroll-height");
      }
      lastPosition = NaN;
      updateArrows();
    };
    const queueMeasurement = () => {
      if (!measureRaf && !disposed) measureRaf = requestAnimationFrame(() => {
        measureRaf = 0;
        runUpdate(configureRail);
      });
    };
    const onPrevious = () => scrollByCard(-1);
    const onNext = () => scrollByCard(1);
    const onRailKeydown = (event) => {
      if (event.target !== rail || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      if (event.key === "Home" || event.key === "End") {
        goToPosition(event.key === "Home" ? 0 : maxScroll());
        return;
      }
      scrollByCard((event.key === "ArrowRight" ? 1 : -1) * (isRtl ? -1 : 1));
    };
    const onFocusIn = (event) => {
      if (section.dataset.scrollMode !== "pinned") return;
      const card = event.target.closest("[data-media-card]");
      if (card) goToPosition(metrics.offsets[cards.indexOf(card)] || 0);
    };
    listen(previous, "click", onPrevious);
    listen(next, "click", onNext);
    listen(rail, "scroll", queuePositionUpdate, { passive: true });
    listen(rail, "scrollend", queuePositionUpdate, { passive: true });
    listen(rail, "keydown", onRailKeydown);
    listen(rail, "focusin", onFocusIn);
    listen(window, "scroll", queuePositionUpdate, { passive: true });
    listen(window, "resize", queueMeasurement, { passive: true });
    if (reducedMotion.addEventListener) listen(reducedMotion, "change", queueMeasurement);
    const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(queueMeasurement) : null;
    rollbackObserver = resizeObserver;
    resizeObserver?.observe(viewport);
    // Header/ancestor height changes (including the size chooser and fonts) move the pin start.
    if (resizeObserver) {
      resizeObserver.observe(document.body);
      if (sticky) resizeObserver.observe(sticky);
      const header = document.querySelector("#shopify-section-header");
      if (header) resizeObserver.observe(header);
    }
    listen(window.visualViewport, "resize", queueMeasurement, { passive: true });
    listen(window, "pageshow", queueMeasurement);
    if (document.fonts?.addEventListener) listen(document.fonts, "loadingdone", queueMeasurement);
    document.fonts?.ready.then(queueMeasurement);
    const onAssetLoad = queueMeasurement;
    // Capture all image loads: content above this section also changes its document offset.
    listen(document, "load", onAssetLoad, true);
    listen(section, "vorug:media-cleanup", () => {
      abort.abort();
      disposed = true;
      setPinActive(false);
      cancelAnimationFrame(updateRaf);
      cancelAnimationFrame(measureRaf);
      resizeObserver?.disconnect();
      removalObserver?.disconnect();
      previous?.removeEventListener("click", onPrevious);
      next?.removeEventListener("click", onNext);
      rail.removeEventListener("scroll", queuePositionUpdate);
      rail.removeEventListener("scrollend", queuePositionUpdate);
      rail.removeEventListener("keydown", onRailKeydown);
      rail.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("scroll", queuePositionUpdate);
      window.removeEventListener("resize", queueMeasurement);
      reducedMotion.removeEventListener?.("change", queueMeasurement);
      document.removeEventListener("load", onAssetLoad, true);
      window.visualViewport?.removeEventListener("resize", queueMeasurement);
      window.removeEventListener("pageshow", queueMeasurement);
      document.fonts?.removeEventListener?.("loadingdone", queueMeasurement);
      delete section.dataset.mediaRailReady;
    }, { once: true });
    if ("MutationObserver" in window && document.body) {
      removalObserver = new MutationObserver(() => {
        if (!section.isConnected) section.dispatchEvent(new Event("vorug:media-cleanup"));
      });
      removalObserver.observe(document.body, { childList: true, subtree: true });
    }
    configureRail();

    const demo = section.querySelector("[data-room-lab-demo]");
    if (!demo) return;
    const product = readProduct(section);
    const format = section.dataset.roomLabFormat;
    const rooms = ROOM_SETS[format] || ROOM_SETS.rug;
    const roomImage = demo.querySelector("[data-demo-room]");
    const stage = demo.querySelector("[data-demo-stage]");
    const changeRoom = demo.querySelector("[data-demo-change-room]");
    const zoom = demo.querySelector("[data-demo-zoom]");
    const variantLabel = demo.querySelector("[data-demo-variant]");
    let roomIndex = 0;
    const renderRoom = () => {
      const room = rooms[roomIndex];
      const roomKey = `room${room[0].toUpperCase()}${room.slice(1)}`;
      const roomSource = section.dataset[roomKey];
      roomImage.src = roomSource;
      roomImage.alt = `${ROOM_LABELS[room]} sample room`;
      stage.dataset.demoRoomName = room;
      changeRoom.setAttribute("aria-label", `Change room. Current room: ${ROOM_LABELS[room]}`);
    };
    const syncVariant = () => {
      const id = selectedVariantId();
      const variant = product?.variants?.find((candidate) => String(candidate.id).split("/").pop() === id);
      if (variant) variantLabel.textContent = variant.title;
    };
    listen(changeRoom, "click", () => {
      roomIndex = (roomIndex + 1) % rooms.length;
      renderRoom();
    });
    listen(zoom, "click", () => {
      const pressed = zoom.getAttribute("aria-pressed") !== "true";
      zoom.setAttribute("aria-pressed", String(pressed));
      stage.classList.toggle("is-zoomed", pressed);
    });
    listen(demo.querySelector("[data-room-lab-demo-open]"), "click", () => {
      const launcher = document.querySelector(`[data-vorug-room-lab-launcher][data-product-id="${section.dataset.productId}"] [data-room-lab-open]:not([disabled])`);
      launcher?.click();
    });
    listen(document, "change", syncVariant);
    listen(document, "variant:change", syncVariant);
    renderRoom();
    syncVariant();
    } catch (error) {
      disposed = true;
      abort.abort();
      cancelAnimationFrame(updateRaf);
      cancelAnimationFrame(measureRaf);
      rollbackObserver?.disconnect();
      removalObserver?.disconnect();
      if (originalRail) originalCards.forEach(node => originalRail.appendChild(node));
      originalAttributes.forEach((snapshot, node) => restoreAttributes(node, snapshot));
      variantLabels.forEach((value, node) => { node.textContent = value; });
      if (originalSectionStyle === null) section.removeAttribute("style"); else section.setAttribute("style", originalSectionStyle);
      if (originalRail) { if (originalRailStyle === null) originalRail.removeAttribute("style"); else originalRail.setAttribute("style", originalRailStyle); }
      delete section.dataset.mediaRailReady;
      delete section.dataset.pdpOptimized;
      console.error("[vorug-media-initialization]", error);
    }
  };

  if (earlyPass) {
    document.querySelectorAll("[data-vorug-product-media]").forEach(section => {
      const cards = [...section.querySelectorAll("[data-media-card]")];
      const removals = planFinishCards(section, cards);
      if (removals?.length) initialize(section);
    });
    return;
  }
  const globalAbort = new AbortController();
  const safely = (name, fn) => { try { fn(); } catch (error) { console.error(`[vorug-${name}]`, error); } };
  try {
    document.addEventListener("shopify:section:load", (event) => {
      safely("gallery", initGalleries);
      event.target.querySelectorAll?.("[data-vorug-product-media]").forEach(section => {
        safely("rail-plan", () => {
          const cards = [...section.querySelectorAll("[data-media-card]")];
          const removals = planFinishCards(section, cards);
          if (removals?.length) initialize(section);
        });
      });
      safely("policies", () => enhancePolicies(event.target));
      safely("help", () => enhanceHelpPopup(event.target));
    }, { capture: true, signal: globalAbort.signal });
    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll?.('[id^="MediaGallery-"]').forEach((gallery) => gallery._vorugGallery?.destroy());
      event.target.querySelectorAll?.("[data-vorug-product-media]").forEach((section) => section.dispatchEvent(new Event("vorug:media-cleanup")));
      event.target.querySelectorAll?.("[data-vorug-pdp] .vorug-pdp-policies accordion-block").forEach((block) => block.dispatchEvent(new Event("vorug:policy-cleanup")));
    }, { signal: globalAbort.signal });
    document.addEventListener("variant:change", () => requestAnimationFrame(initGalleries), { signal: globalAbort.signal });
    window.addEventListener("pageshow", (event) => { if (event.persisted) initGalleries(); }, { signal: globalAbort.signal });
    window.__vorugPdpMediaLoaded = true;
  } catch (error) {
    globalAbort.abort();
    console.error("[vorug-global-setup]", error);
    return;
  }
  document.querySelectorAll("[data-vorug-product-media]").forEach(section => safely("rail", () => initialize(section)));
  safely("policies", enhancePolicies);
  safely("help", enhanceHelpPopup);
  safely("gallery", initGalleries);
})();
